import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormInput,
  NotificationsContext,
  AutocompleteInput,
  ConfigurationContext,
  FormGetter,
  Grid,
  SelectInput,
  DateInput,
  HiddenInput,
} from '@kirz/mui-admin';
import { Print } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Typography, Box, IconButton, Tooltip, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { FolderOpen } from 'mdi-material-ui';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { api } from 'services/api';

import { UploadsTable } from './UploadsTable';

type PrintCompanyButtonProps = {
  id: number;
  bkh: string;
  companyName: string;
  numbersOfFilesToPrint: Record<string, any>;
  onPrinted: () => void;
};

function PrintCompanyButton({
  id,
  bkh,
  companyName,
  numbersOfFilesToPrint,
  onPrinted,
}: PrintCompanyButtonProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { hasura } = useContext(ConfigurationContext);
  const { showPrompt } = useContext(NotificationsContext);

  const printCompany = async () => {
    try {
      setIsLoading(true);

      const [
        {
          data: { printers },
        },
        directories,
      ] = await Promise.all([
        api.client.get<{ printers: string[] }>(`/files/printers`),
        hasura.request({
          type: 'query',
          selection: 'id name',
          source: 'directory',
          orderBy: { createdAt: 'DESC' },
        }),
      ]);

      const lastUsedPrinter = localStorage.getItem(`last-used-printer`);

      const values = await showPrompt({
        title: 'Afdrukgegevens',
        form: (
          <>
            <AutocompleteInput
              name="printer"
              label="Printer"
              options={printers.map((x) => ({ text: x, value: x }))}
              mode="memory"
              required
              autoFocus
            />
            <AutocompleteInput
              name="directory"
              label="Directory"
              options={directories
                .filter(
                  (x: any) =>
                    (numbersOfFilesToPrint[`${x.name}_info`]?.numberOfPdfs ??
                      0) > 0,
                )
                .map((x: any) => ({
                  text: `${x.name}[${
                    numbersOfFilesToPrint[`${x.name}_info`]?.numberOfPdfs ?? 0
                  }]`,
                  value: x.name,
                }))}
              mode="memory"
              required
            />
            <AutocompleteInput
              name="numberOfFilesToPrint"
              label="Aantal documenten"
              options={[
                { text: '10', value: 10 },
                { text: '20', value: 20 },
                { text: '50', value: 50 },
                { text: 'Allemaal', value: 'all' },
              ]}
              mode="memory"
              required
            />
          </>
        ),
        formProps: {
          defaultValues: {
            printer:
              lastUsedPrinter && printers.includes(lastUsedPrinter)
                ? lastUsedPrinter
                : null,
            numberOfFilesToPrint: 10,
          },
        },
        accept: 'Print',
        width: 500,
      });

      // @ts-ignore
      const printer = values?.printer as string;

      if (!printer) {
        return;
      }

      await api.client.post(`/files/print-company`, {
        bkh,
        companyName,
        printer,
        // @ts-ignore
        directory: values?.directory as string,
        // @ts-ignore
        numberOfFilesToPrint: values?.numberOfFilesToPrint,
      });

      localStorage.setItem(`last-used-printer`, printer);

      onPrinted();
    } finally {
      setIsLoading(false);
    }
  };

  const infoKeys = Object.keys(numbersOfFilesToPrint).filter((x) =>
    x.endsWith('_info'),
  );

  const numberOfFilesToPrint = infoKeys
    .map((key) => numbersOfFilesToPrint[key].numberOfPdfs)
    .reduce((a, b) => a + b, 0);

  return (
    <>
      <LoadingButton
        variant="contained"
        startIcon={<Print />}
        size="small"
        loading={isLoading || numberOfFilesToPrint === null}
        disabled={!numberOfFilesToPrint}
        onClick={printCompany}
        sx={{
          minWidth: 100,
        }}
      >
        {numberOfFilesToPrint} documenten
      </LoadingButton>
    </>
  );
}

const getInfoSum = (
  numbersOfFilesToPrint: Record<string, any>,
  field: string,
) => {
  return `${numbersOfFilesToPrint?.['facturen_uit_info']?.[field] ?? 0}/${
    numbersOfFilesToPrint?.['facturen_in_info']?.[field] ?? 0
  }`;
};

export const CompaniesTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { filterColumns, ...rest } = props;

    const tableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'id',
            headerName: 'ID',
            width: 80,
            renderCell: ({ row }) => {
              return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Open map">
                    <IconButton
                      component="a"
                      href={`localexplorer:O:\\${row.bkh}`}
                      target="_blank"
                      size="small"
                    >
                      <FolderOpen fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <span>{row.id}</span>
                </Stack>
              );
            },
          },
          {
            field: 'name',
            headerName: 'Naam',
            flex: 1,
          },
          {
            field: 'bkh',
            headerName: 'BKH',
            flex: 1,
          },
          {
            field: 'vatNumber',
            headerName: 'BTW nummer',
            flex: 1,
          },
          {
            field: 'peppolRegisteredAt',
            headerName: 'PEPPOL',
            flex: 1,
            type: 'boolean',
            renderCell({ row }) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: row.peppolRegisteredAt ? 'green' : 'red',
                    }}
                  />
                </Box>
              );
            },
          },
          {
            field: 'address',
            headerName: 'Adres',
            flex: 1,
          },
          {
            field: 'next_meeting_date',
            headerName: 'Volgende vergadering op',
            type: 'date',
            flex: 1,
          },
          {
            field:
              'clientsAggregate(where:{isRemoved:{_eq: false}}) { aggregate { *count* } }',
            headerName: 'Aantal klanten',
            flex: 1,
            type: 'relationship',
            fetchRemoved: false,
          },
          {
            field: 'xml',
            headerName: 'XML (V/A)',
            selector: false,
            sortable: false,
            width: 90,
            renderCell: (params) =>
              getInfoSum(params.row.numbersOfFilesToPrint, 'numberOfXmls'),
          },
          {
            field: 'errors',
            selector: false,
            sortable: false,
            headerName: 'Errors (V/A)',
            width: 100,
            renderCell: (params) =>
              getInfoSum(params.row.numbersOfFilesToPrint, 'numberOfErrors'),
          },
          {
            field: 'numbersOfFilesToPrint',
            headerName: 'Afdrukken',
            width: 200,
            onSort(sort) {
              return [{ numberOfFilesToPrint: sort }];
            },
            renderCell: (params) => {
              return (
                <PrintCompanyButton
                  id={params.row.id}
                  bkh={params.row.bkh}
                  companyName={params.row.name}
                  numbersOfFilesToPrint={params.row.numbersOfFilesToPrint}
                  onPrinted={() => tableRef.current?.reload()}
                />
              );
            },
          },
          { field: 'createdAt', headerName: 'Aangemaakt op', type: 'date' },
        ]),
      [filterColumns],
    );

    useEffect(() => {
      const interval = setInterval(() => {
        tableRef.current?.reload();
      }, 10000);

      return () => clearInterval(interval);
    }, []);

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="company"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
        editable={{
          onEdit(row) {
            tableRef.current?.openFormDialog(row);
          },
        }}
        formTitle={(isNew) => (isNew ? 'Nieuw bedrijf' : 'Bedrijf bewerken')}
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'md',
        }}
      >
        <FormInput name="name" label="Naam" required />
        <FormInput name="bkh" label="BKH" required />
        <FormInput name="vatNumber" label="BTW nummer" required />
        <FormInput name="address" label="Adres" required />
        <DateInput name="establishmentDate" label="Oprichtingsdatum" />
        <Grid xs={12} container>
          <HiddenInput
            name="next_meeting_date"
            formSubmitterValueResolver={null}
          />
          <Grid xs={12}>
            <Typography variant="subtitle1">
              Algemene Vergadering
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                <FormGetter
                  names={['next_meeting_date']}
                  render={(x) =>
                    `Volgende vergadering op ${
                      x.next_meeting_date
                        ? dayjs(x.next_meeting_date).format('DD.MM.YYYY')
                        : 'N/A'
                    }`
                  }
                />
              </Typography>
            </Typography>
          </Grid>

          <SelectInput
            name="generalMeetingEvery"
            label="Elke"
            items={[
              { text: 'eerste', value: '1' },
              { text: 'tweede', value: '2' },
              { text: 'derde', value: '3' },
              { text: 'vierde', value: '4' },
            ]}
            md={4}
            clearable
          />
          <SelectInput
            name="generalMeetingWeekday"
            label="Weekdag"
            items={[
              { text: 'maandag', value: 1 },
              { text: 'dinsdag', value: 2 },
              { text: 'woensdag', value: 3 },
              { text: 'donderdag', value: 4 },
              { text: 'vrijdag', value: 5 },
              { text: 'zaterdag', value: 6 },
              { text: 'zondag', value: 7 },
            ]}
            md={4}
            clearable
          />
          <SelectInput
            name="generalMeetingMonth"
            label="Maand"
            items={[
              { text: 'januari', value: 1 },
              { text: 'februari', value: 2 },
              { text: 'maart', value: 3 },
              { text: 'april', value: 4 },
              { text: 'mei', value: 5 },
              { text: 'juni', value: 6 },
              { text: 'juli', value: 7 },
              { text: 'augustus', value: 8 },
              { text: 'september', value: 9 },
              { text: 'oktober', value: 10 },
              { text: 'november', value: 11 },
              { text: 'december', value: 12 },
            ]}
            md={4}
            clearable
          />
        </Grid>
        <FormGetter
          names={['id', 'name']}
          render={(x) =>
            x.id && (
              <Grid xs={12}>
                <UploadsTable
                  id="company-uploads-table"
                  companyId={x.id}
                  title="Permanent Dossier"
                />
              </Grid>
            )
          }
        />
      </DataTableEx>
    );
  },
);
